import { BalanceSheetModel } from "@/models/balanceSheet.model";
import { Category, CategoryWithZone } from "@/models/categories.model";
import { Settings } from "@/models/settings.model";
import { DepressionCalculatedValues, ElementaryZone, SiteModel } from "@/models/site.model";
import {
  calculateBalanceSheet,
  calculateDepressionCalculatedValuesPerZone,
} from "@/services/balanceSheet.service";
import { Equipment, Extractor } from "@/models/equipment.model";
import store from "@/store";
import { Component, Prop, Vue } from "vue-property-decorator";
import EquipmentCollapse from "./parts/EquipmentCollapse/index.vue";
import useVuelidate, { Validation } from "@vuelidate/core";
import { SEND_JSON_STATISTICS, UPDATE_CUSTOM_EQUIPMENT_LABEL } from "@/store/operators";
import { abacaEvent, getEventJsonData } from "@/services/statistics.service";
import { ref } from "@vue/composition-api";
import { verifSiteEacmOptional } from "@/services/equipment.service";

@Component({
  components: { EquipmentCollapse },
  setup() {
    return { v: useVuelidate() };
  },
})
export default class EquipmentComponent extends Vue {
  @Prop() bus!: Vue;

  //datas
  categoriesWithZone = this.buildCategoriesWithZones();
  v!: Validation;
  tooltipHelpStepperSiteSction = this.$sanitize(
    this.$t("help.equipmentPage.stepperSiteSection.thirdStep").toString()
  );

  beforeCreate(): void {
    const constructorCustomEquipment = this.$t(
      "configurations.equipmentsPage.customEquipment.constructorCustomEquipment"
    ).toString();
    store.commit(UPDATE_CUSTOM_EQUIPMENT_LABEL, constructorCustomEquipment);
  }

  // get categoryList from store
  get categoryList(): Category[] {
    return store.state.globalAppDataModule.categories;
  }

  get settings(): Settings {
    return store.state.settingsModule.settings;
  }

  get site(): SiteModel {
    return store.state.siteModule.site;
  }

  get eacmEquipment(): Equipment {
    return store.state.equipmentsModule.eacmEquipment;
  }

  //Evolution Majeure de EACM
  get optionalEacmEquipment(): Equipment {
    return store.state.equipmentsModule.optionalEacmEquipment;
  }

  get mandatoryExtractor(): Extractor {
    return store.state.equipmentsModule.mandatoryExtractor;
  }

  get optionalExtractor(): Extractor {
    return store.state.equipmentsModule.optionalExtractor;
  }
  get mandatoryExtractorCount(): number {
    return store.state.equipmentsModule.mandatoryExtractorCount;
  }

  get isSecurityExtractorRequired(): boolean {
    return store.state.equipmentsModule.isSecurityExtractorRequired;
  }

  get optionalExtractorCount(): number {
    return store.state.equipmentsModule.optionalExtractorCount;
  }

  get earEquipment(): Equipment {
    return store.state.equipmentsModule.earEquipment;
  }

  mounted(): void {
    this.bus.$on("validate", this.validate.bind(this));
    store.dispatch(SEND_JSON_STATISTICS, getEventJsonData(abacaEvent.EQUIPMENTS));

  }

  // unsubrcribe all event on destroy
  destroyed(): void {
    this.bus.$off();
  }

  // validate step and send it to store
  validate(): void {

      /*Evolution Majeure composition de EACM*/
    let eacmForCalcul:Equipment=this.eacmEquipment;
    const optionalEacmEquipment = store.state.equipmentsModule.optionalEacmEquipment;
      /* fin evolution Majeure de EACM*/

    this.site.elementaryZones.forEach((el) => {
      const siteAVerifier = verifSiteEacmOptional( el);
        /*Evolution Majeure composition de EACM*/
      if(optionalEacmEquipment && siteAVerifier==true) {
        eacmForCalcul = this.optionalEacmEquipment;
        el.optional = true

      }
      else {
        eacmForCalcul = this.eacmEquipment;
        el.optional = false;
      }
      el = calculateDepressionCalculatedValuesPerZone(el, this.settings, eacmForCalcul);
        /* fin evolution Majeure de EACM*/

     // el = calculateDepressionCalculatedValuesPerZone(el, this.settings, this.eacmEquipment);
    });
    const balanceSheet: BalanceSheetModel = calculateBalanceSheet(
      this.site,
      this.settings,
      this.earEquipment,
      {
        mandatoryExtractor: this.mandatoryExtractor,
        optionalExtractor: this.optionalExtractor,
        mandatoryExtractorAvailable: this.mandatoryExtractorCount,
        optionalExtractorAvailable: this.optionalExtractorCount,
        isSecurityExtractorRequired: this.isSecurityExtractorRequired,
      }
    );
    store.commit("UPDATE_BALANCE_SHEET", balanceSheet);
    store.commit("UPDATE_SITE_MODEL", this.site);
  }

  /**
   * Build the list of categories with their corresponding zones. The user can select 1 equipment by zone.
   * We'll pass this array to Equipment Collapse component. For Each zone in the category object, it will create one EquipmentDisplay Component.
   * However only decontaminations categories (first and second) are linked to a zone, for the ohers we create a default zone to tell how many components should be rendered.
   * First zone is workers decontamination
   * Second zone is waste decontamination
   * Third zone is extractors. We use 2 zones to allow 2 extractors to be selected. Only the first one is mandatory
   * Fourth zone is eacm. We use one defaultZone to allow 1 equipment.
   * Fifth zone is ear. We use one defaultZone to allow 1 equipment.
   */
  buildCategoriesWithZones(): CategoryWithZone[] {
    //Copy by value of categoryList from store.

    const categories: CategoryWithZone[] = store.state.globalAppDataModule.categories
      .map((category: Category) => {
        return {
          ...category,
          correspondingZones: [],
        };
      })
      .sort(
        (category1: CategoryWithZone, category2: CategoryWithZone) =>
          category1.order > category2.order
      );
      

    categories[0].correspondingZones = store.getters.GET_CLEAN_WORKERS_ELEMENTARY_ZONES;
    categories[1].correspondingZones = store.getters.GET_CLEAN_WASTES_ELEMENTARY_ZONES;
    const defaultZone: ElementaryZone = {
      name: "defaultZone",
      personnelDecontamination: false,
      wasteDecontamination: false,
      extractorsInstallation: false,
      minimalDepressionValues: new DepressionCalculatedValues(),
      depressionToMaintainValues: new DepressionCalculatedValues(),
      volume: null,
    };
    categories[2].correspondingZones = [
      {
        ...defaultZone,
        name: "mandatory",
        customEquipment: store.state.equipmentsModule.mandatoryExtractor,
      },
      {
        ...defaultZone,
        name: "optional",
        isMandatory: false,
        customEquipment: store.state.equipmentsModule.optionalExtractor,
      },
    ];
    categories[3].correspondingZones = [
      { ...defaultZone,
         name: "eacm",
         customEquipment: store.state.equipmentsModule.eacmEquipment,
      },
      { ...defaultZone,
         name: "eacm optional" ,
         isMandatory: false,
         customEquipment: store.state.equipmentsModule.optionalEacmEquipment, /*Evolution Majeure composition de EACM*/
      },

    ];
    categories[4].correspondingZones = [
      {
        ...defaultZone,
        name: "ear",
        isMandatory: store.state.equipmentsModule.isEarMandatory,
        customEquipment: store.state.equipmentsModule.earEquipment,
      },
    ];
    const temp = categories[3];
    categories[3] = categories[2];
    categories[2] = temp;
    return categories;
  }
}
